import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../LangContext";

const NavLink = ({ to, children, onClick, testid }) => (
  <a
    href={to}
    data-testid={testid}
    onClick={onClick}
    className="text-sm font-medium text-ink2 hover:text-ink transition-colors duration-300"
  >
    {children}
  </a>
);

export default function Header() {
  const { t, lang, setLanguage } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services, testid: "nav-services" },
    { id: "about", label: t.nav.about, testid: "nav-about" },
    { id: "pricing", label: t.nav.pricing, testid: "nav-pricing" },
    { id: "faq", label: t.nav.faq, testid: "nav-faq" },
    { id: "contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  const goTo = (id) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-cream/85 border-b border-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="logo-link"
          onClick={goTo("top")}
          className="flex items-baseline gap-2"
        >
          <span className="font-serif text-2xl tracking-tight text-ink">Notarius</span>
          <span className="font-serif italic text-2xl text-ink2">Publicus</span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink key={l.id} to={`#${l.id}`} onClick={goTo(l.id)} testid={l.testid}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="hidden sm:flex items-center text-xs tracking-widest uppercase border border-line rounded-full overflow-hidden"
            data-testid="lang-toggle"
          >
            <button
              onClick={() => setLanguage("sv")}
              data-testid="lang-sv"
              className={`px-3 py-1.5 transition-colors ${
                lang === "sv" ? "bg-ink text-cream" : "text-ink2 hover:text-ink"
              }`}
            >
              SV
            </button>
            <button
              onClick={() => setLanguage("en")}
              data-testid="lang-en"
              className={`px-3 py-1.5 transition-colors ${
                lang === "en" ? "bg-ink text-cream" : "text-ink2 hover:text-ink"
              }`}
            >
              EN
            </button>
          </div>

          <a
            href="#contact"
            onClick={goTo("contact")}
            data-testid="header-book-cta"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-cream text-sm font-medium hover:bg-ink2 transition-all duration-300"
          >
            {t.nav.book}
            <motion.span
              initial={{ x: 0 }}
              whileHover={{ x: 3 }}
              className="inline-block"
            >
              →
            </motion.span>
          </a>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 -mr-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
          >
            <span className="block w-6 h-px bg-ink mb-1.5" />
            <span className="block w-6 h-px bg-ink mb-1.5" />
            <span className="block w-4 h-px bg-ink ml-auto" />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-line bg-cream/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={goTo(l.id)}
                data-testid={`mobile-${l.testid}`}
                className="text-lg font-serif text-ink"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={goTo("contact")}
              data-testid="mobile-book-cta"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-full bg-ink text-cream text-sm"
            >
              {t.nav.book}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
